import { useState } from "react";
import styled from "@emotion/styled";
import { Button } from "@mui/material";

import { useAccessToken } from "@/hooks/auth/use-access-token";
import { useCustomTranslation } from "@/utils/useCustomTranslation";

import {
  ScheduleResponseDto,
  ScheduleStatus,
} from "@shared/generated/api/model";

interface EnterClassButtonProps {
  schedule: ScheduleResponseDto;
}

export const EnterClassButton = ({ schedule }: EnterClassButtonProps) => {
  const { t } = useCustomTranslation();
  const [isActionLoading, setIsActionLoading] = useState(false);
  const { accessToken } = useAccessToken();
  // console.log(schedule);
  const handleStartClass = async () => {
    if (!accessToken) {
      alert(t("login_required"));
      return;
    }

    setIsActionLoading(true);
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_HOST}/course/${schedule.cycle.course.id}/schedule/${schedule.id}/enter-live-class/student`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const result = await response.json();

      if (result.error) {
        throw new Error(result.error.message);
      }

      const { url } = result.body;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        throw new Error(t("cannot_enter_classroom"));
      }
    } catch (error) {
      console.error("수업 시작 중 오류 발생:", error);
      alert(error instanceof Error ? error.message : t("cannot_start_class"));
    } finally {
      setIsActionLoading(false);
    }
  };

  const renderButton = () => {
    switch (schedule.status) {
      case ScheduleStatus.PENDING:
        return (
          <StyledButton
            variant="contained"
            disabled={true}
          >
            {t("waiting_to_join_the_class")}
          </StyledButton>
        );

      case ScheduleStatus.MEETING_READY:
      case ScheduleStatus.ONGOING:
      case ScheduleStatus.READY_TO_COMPLETE:
        return (
          <StyledButton
            variant="contained"
            onClick={handleStartClass}
            disabled={isActionLoading}
          >
            {isActionLoading ? t("entering") : t("enter_classroom")}
          </StyledButton>
        );

      // case ScheduleStatus.ONGOING:
      // case ScheduleStatus.READY_TO_COMPLETE:
      //   return (
      //     <StyledButton
      //       variant="contained"
      //       onClick={handleStartClass}
      //     >
      //       {t("re_enter_classroom")}
      //     </StyledButton>
      //   );

      case ScheduleStatus.CANCELLED:
      case ScheduleStatus.EXPIRED:
        return (
          <StyledButton
            variant="contained"
            disabled={true}
          >
            {t("cancel")}
          </StyledButton>
        );

      case ScheduleStatus.COMPLETED:
        return (
          <StyledButton
            variant="contained"
            disabled={true}
          >
            {t("class_ended")}
          </StyledButton>
        );

      default:
        return null;
    }
  };

  return renderButton();
};

const StyledButton = styled(Button)<{ disabled?: boolean | false }>`
  padding: 8px 16px;
  width: 100%;
  max-width: 120px;
  height: 100%;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: none;
  ${({ theme }) => theme.typography.body2.b};
  color: ${({ theme, disabled }) =>
    disabled ? theme.colors.text.disabled.black : theme.colors.text.high.white};
`;
